/*
 * Custom property operations for the Demo Environment Management page.
 * Datasource-level entity. Mirrors sales-demo-env-setup's `src/lib/property.js`,
 * adapted to use `CsqApiClient`. "Create" is always replicated from Source (see
 * `buildPropertiesCreateFromSourceOperations` in entities-tab.js) rather than a
 * hardcoded default formula.
 *
 * NOTE on delete: no delete action was captured in the original HAR this was
 * ported from. `deleteProperty` follows the same REST resource shape as the
 * confirmed list/create calls but is INFERRED — verify with a dry run before
 * relying on it.
 */

/**
 * Creates a custom property in a datasource.
 *
 * @param {import('./csqApiClient').CsqApiClient} client
 * @param {string|number} datasourceId
 * @param {{ name: string, level?: string, formula: object, dataSourceIds?: Array<string|number>, notes?: string, createLocation?: string }} data
 * @returns {Promise<object>} The created property (includes `id`).
 */
export async function createProperty(
  client,
  datasourceId,
  { name, level = 'event', formula, dataSourceIds, notes = '', createLocation = 'csq_properties_view' },
) {
  return client.post(`/api/pa-metadata/v1/projects/${datasourceId}/defined-property-metadata`, {
    name,
    level,
    formula,
    dataSourceIds: (dataSourceIds || [datasourceId]).map(String),
    notes,
    createLocation,
  });
}

/**
 * Lists custom properties in a datasource.
 *
 * @param {import('./csqApiClient').CsqApiClient} client
 * @param {string|number} datasourceId
 * @param {object} [options]
 * @param {string} [options.level] - "event" or "user" (default "event").
 * @param {Array<string|number>} [options.dataSourceIds] - defaults to `[datasourceId]`.
 * @param {string} [options.search] - Free-text filter.
 * @param {number} [options.take] - Page size (0 = no limit).
 * @param {number} [options.skip] - Offset for pagination (default 0).
 * @returns {Promise<Array<{ id: string, name: string }>>}
 */
export async function listProperties(client, datasourceId, { level = 'event', dataSourceIds, search = '', take = 0, skip = 0 } = {}) {
  const query = new URLSearchParams();
  query.append('order', 'name');
  query.append('text', search);
  query.append('skip', String(skip));
  query.append('take', String(take));
  query.append('withCount', 'true');
  query.append('level', level);
  (dataSourceIds || [datasourceId]).forEach((id) => query.append('dataSourceIds', String(id)));

  const payload = await client.get(`/api/pa-metadata/v1/projects/${datasourceId}/defined-property-metadata?${query.toString()}`);
  const items = (payload && payload.items) || [];
  return items.map((item) => ({ id: item.id, name: item.name }));
}

/**
 * Lists custom properties in a datasource with their full `formula`
 * definition (and `level`/`notes`), instead of just `{id, name}`. Used to
 * recreate a datasource's custom properties on another datasource (see
 * "Create from Source" in entities-tab.js).
 *
 * @param {import('./csqApiClient').CsqApiClient} client
 * @param {string|number} datasourceId
 * @param {object} [options]
 * @param {string} [options.level] - "event" or "user" (default "event").
 * @param {Array<string|number>} [options.dataSourceIds] - defaults to `[datasourceId]`.
 * @returns {Promise<Array<{ id: string, name: string, level: string, formula: object, notes: string }>>}
 */
export async function listPropertiesWithFormula(client, datasourceId, { level = 'event', dataSourceIds } = {}) {
  const query = new URLSearchParams();
  query.append('order', 'name');
  query.append('text', '');
  query.append('skip', '0');
  query.append('take', '0');
  query.append('withCount', 'true');
  query.append('level', level);
  (dataSourceIds || [datasourceId]).forEach((id) => query.append('dataSourceIds', String(id)));

  const payload = await client.get(`/api/pa-metadata/v1/projects/${datasourceId}/defined-property-metadata?${query.toString()}`);
  const items = (payload && payload.items) || [];
  return items.map((item) => ({
    id: item.id,
    name: item.name,
    level: item.level || level,
    formula: item.formula,
    notes: item.notes || '',
  }));
}

/**
 * Lists custom properties across BOTH the "event" and "user" levels, combined
 * into a single array, each with its full `formula` definition. Used to
 * recreate every custom property from a datasource (see "Create from Source"
 * in entities-tab.js) — mirrors csqEntityHandlers.js's internal
 * `listAllDatasourceProperties`, but with full replication detail instead of
 * just `{id, name}`.
 *
 * @param {import('./csqApiClient').CsqApiClient} client
 * @param {string|number} datasourceId
 * @returns {Promise<Array<{ id: string, name: string, level: string, formula: object, notes: string }>>}
 */
export async function listAllPropertiesWithFormula(client, datasourceId) {
  const [eventItems, userItems] = await Promise.all([
    listPropertiesWithFormula(client, datasourceId, { level: 'event' }),
    listPropertiesWithFormula(client, datasourceId, { level: 'user' }),
  ]);
  return [...eventItems, ...userItems];
}

/**
 * Deletes a custom property by id. INFERRED endpoint — see module header comment.
 */
export async function deleteProperty(client, datasourceId, propertyId) {
  return client.del(`/api/pa-metadata/v1/projects/${datasourceId}/defined-property-metadata/${propertyId}`);
}
